import { Box, Button, Container, Flex, Grid, Heading, Text } from '@chakra-ui/react';
import { StarBackground } from '@components/StarBackground';
import { APIGetKongzResponse } from '@cyberkongz/api-interfaces';
import {
  AppLink,
  AppSpinner,
  getKongImageByType,
  Image,
  KollectionsControlsWrapper,
  KollectionsFiltersPills,
  KollectionsProvider,
  KollectionsSearchBox,
  pxToRem,
  TabsContainer,
  TabsItem,
  useKollections,
} from '@cyberkongz/ui';
import { PageSEOTags } from '@features/seo/SEO';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { KOLLECTIONS_STRING_TRAITS } from './data';
import { KollectionsBanner } from './KollectionsBanner';
import { KollectionsFilters } from './KollectionsFilters';
import { KollectionsSort } from './KollectionsSort';
import { LandingKollectionType } from './types';
import { APP_ROUTES } from '@cyberkongz/ui/src/routes/appRoutes';

const TABS: { label: string; id: LandingKollectionType }[] = [
  {
    label: 'Genesis',
    id: 'genesis',
  },
  {
    label: 'Baby',
    id: 'baby',
  },
  {
    label: 'VX',
    id: 'vx',
  },
];

function KollectionsTabs() {
  const { kollection, setKollection } = useKollections();

  return useMemo(
    () => (
      <TabsContainer>
        {TABS.map(({ id, label }) => (
          <TabsItem key={id} {...{ activeTab: kollection, setActiveTab: setKollection, tab: id }}>
            <Flex css={{ alignItems: 'center' }}>
              <Image
                src={`/static/icons/avatar_${id}_transparent.png`}
                alt={`${label} avatar`}
                css={{ maxW: [6, '100%'] }}
              />
              <Heading size="2xs" css={{ ml: [2, 3], color: 'inherit' }}>
                {label}
              </Heading>
            </Flex>
          </TabsItem>
        ))}
      </TabsContainer>
    ),
    [kollection]
  );
}

function Results({
  fetchNextPage,
  hasNextPage,
  pages,
}: {
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  pages: APIGetKongzResponse[];
}) {
  return (
    <InfiniteScroll
      scrollThreshold="900px"
      dataLength={pages.length}
      hasMore={hasNextPage || false}
      next={fetchNextPage}
      loader={
        <Box css={{ textAlign: 'center', mx: 'auto', mt: 4 }}>
          <AppSpinner />
        </Box>
      }
    >
      <Grid
        gap={[3, 4, 5]}
        gridTemplateColumns={['repeat(2, 1fr)', 'repeat(4, 1fr)', 'repeat(5, 1fr)']}
      >
        {pages.map(({ kollection, kongz }) =>
          kongz.map(({ id, name }) => (
            <AppLink
              key={id}
              href={APP_ROUTES.KONG(kollection, id)}
              css={{ textDecoration: 'none' }}
            >
              <Grid gap={2}>
                <Image
                  src={getKongImageByType(id, kollection, { sizeVX: 'medium' })}
                  css={{
                    clipPath: 'polygon(88% 0, 100% 12%, 100% 100%, 0 100%, 0 0)',
                    width: '100%',
                  }}
                  alt={`Kong ${id} preview`}
                />
                <Box
                  css={{
                    textTransform: 'uppercase',
                    fontWeight: 'medium',
                    textAlign: 'center',
                    fontSize: 'sm',
                  }}
                >
                  {name ? name : kollection === 'vx' ? 'Cyberkong VX' : 'Cyberkong'} #{id}
                </Box>
              </Grid>
            </AppLink>
          ))
        )}
      </Grid>
    </InfiniteScroll>
  );
}

function NoResults() {
  const { clearFilters } = useKollections();

  return (
    <Box css={{ textAlign: 'center', mt: 16 }}>
      <Heading size="sm">No results found</Heading>
      <Text mt={1} mb={6}>
        Try adjusting your search to find what you&apos;re looking for.
      </Text>
      <Button onClick={clearFilters} variant="display">
        clear filters
      </Button>
    </Box>
  );
}

function KollectionsView() {
  const { query } = useKollections();

  return (
    <>
      <StarBackground
        stylesContainer={{
          position: 'fixed',
          top: 0,
          left: 0,
          w: '100%',
          h: '100%',
          zIndex: -1,
          '&:before': {
            display: 'block',
            position: 'absolute',
            content: '""',
            opacity: 0.4,
            bottom: 0,
            right: 0,
            w: '100%',
            h: '0',
            maxWidth: pxToRem(1440),
            pb: '86.18%',
            bg: 'linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), radial-gradient(74.83% 85.41% at 80.9% 78.69%, #017C8F 0%, #000000 100%)',
          },
        }}
      />
      <Box width="100%" pt={[24, pxToRem(138)]} pb={20}>
        <Container color="textSecondary" position="relative">
          <Grid gap={[5, null, 6]}>
            <KollectionsTabs />
            <KollectionsControlsWrapper>
              <Flex
                alignItems={['flex-start', 'center']}
                justifyContent="space-between"
                flexDirection={['column', 'row']}
                gap={[2, 4]}
              >
                <Flex
                  width={['100%', 'initial']}
                  alignItems="center"
                  justifyContent={['space-between', 'flex-start']}
                  gap={4}
                  flex={1}
                >
                  <KollectionsFilters />
                  <KollectionsSearchBox />
                </Flex>
                <Flex
                  width={['100%', 'initial']}
                  alignItems="center"
                  justifyContent={['space-between', 'flex-start']}
                  gap={4}
                >
                  <Box>{query?.data ? query.data.pages[0].count : '-'} Kongz</Box>
                  <KollectionsSort />
                </Flex>
              </Flex>
              <KollectionsFiltersPills />
            </KollectionsControlsWrapper>
            <KollectionsBanner />
            {query?.data ? (
              query.data.pages.length > 0 ? (
                <Results
                  {...{
                    hasNextPage: query.hasNextPage,
                    fetchNextPage: query.fetchNextPage,
                    pages: query.data.pages,
                  }}
                />
              ) : (
                <NoResults />
              )
            ) : (
              <AppSpinner />
            )}
          </Grid>
        </Container>
      </Box>
    </>
  );
}

export function Kollections() {
  const router = useRouter();

  // TO DO, find nicer way to get initial query params being available on load
  // https://nextjs.org/docs/api-reference/next/router
  return router.isReady ? (
    <KollectionsProvider kollectionsStringTraits={KOLLECTIONS_STRING_TRAITS}>
      <PageSEOTags pageKey="kollections" />
      <KollectionsView />
    </KollectionsProvider>
  ) : null;
}
